import { useState } from "react";
import type { AskUserQuestionAnswer, Message, ReferenceItem } from "../../api/types.js";
import { ProcessPocket } from "../activity/ActivityProcess.js";
import { AskUserQuestionCard } from "./AskUserQuestionCard.js";
import { MarkdownAnswer } from "./MarkdownAnswer.js";
import { MessageSkillLabel, messageSelectedSkill } from "./MessageSkillLabel.js";
import { processElapsed, processItems, processStatus } from "./processTimeline.js";
import { citedSourcesForMessage } from "../sources/citationSources.js";

function UserMessageBubble({ message }: { message: Message }) {
  const skill = messageSelectedSkill(message.meta);
  return (
    <div className="bubble">
      {skill && <MessageSkillLabel skill={skill} />}
      <div className="user-message-content">{message.content}</div>
    </div>
  );
}

export function ChatThread({
  messages,
  citationSources,
  referencePoolTotalCount,
  expandedActivityMessageId,
  setExpandedActivityMessageId,
  onOpenCitation,
  onOpenMessageSources,
  onOpenReferencePool,
  setPrompt,
  onSubmitAskUserQuestion,
  onCancelAskUserQuestion,
  saveSourceAsAsset
}: {
  messages: Message[];
  citationSources: ReferenceItem[];
  referencePoolTotalCount: number;
  expandedActivityMessageId: string | null;
  setExpandedActivityMessageId: (messageId: string | null) => void;
  onOpenCitation: (messageId: string, localIndex: number) => void;
  onOpenMessageSources: (messageId: string) => void;
  onOpenReferencePool: (messageId: string) => void;
  setPrompt: (prompt: string) => void;
  onSubmitAskUserQuestion: (message: Message, answers: AskUserQuestionAnswer[]) => void;
  onCancelAskUserQuestion: (message: Message) => void;
  saveSourceAsAsset: (source: ReferenceItem) => void;
}) {
  const [collapsedLiveMessageIds, setCollapsedLiveMessageIds] = useState<Set<string>>(() => new Set());
  const openCitation = (index: number, message: Message) => {
    onOpenCitation(message.id, index);
  };

  return (
    <div className="thread" aria-live="polite">
      {messages.map((message) => (
        <article className={`message ${message.role}`} key={message.id}>
          {message.role === "user" ? <UserMessageBubble message={message} /> : (
            <div className="assistant-answer">
              {(() => {
                const items = processItems(message);
                const status = processStatus(message);
                const live = status === "running" || status === "waiting";
                const expanded = live
                  ? !collapsedLiveMessageIds.has(message.id)
                  : expandedActivityMessageId === message.id;
                return (
                  <>
                    {(items.length > 0 || live) && (
                      <ProcessPocket
                        items={items}
                        status={status}
                        elapsedMs={processElapsed(message, items)}
                        expanded={expanded}
                        onToggle={() => {
                          if (live) {
                            setCollapsedLiveMessageIds((old) => {
                              const next = new Set(old);
                              if (next.has(message.id)) next.delete(message.id);
                              else next.add(message.id);
                              return next;
                            });
                            return;
                          }
                          setExpandedActivityMessageId(expanded ? null : message.id);
                        }}
                      />
                    )}
                    <MarkdownAnswer message={message} onCitation={openCitation} />
                  </>
                );
              })()}
              {message.meta?.askUserQuestion && (message.meta?.askUserQuestionStatus || "pending") !== "pending" && (
                <AskUserQuestionCard
                  ask={message.meta.askUserQuestion}
                  status={message.meta.askUserQuestionStatus || "pending"}
                  answers={message.meta.askUserQuestionAnswers || []}
                  onSubmit={(answers) => onSubmitAskUserQuestion(message, answers)}
                  onCancel={() => onCancelAskUserQuestion(message)}
                />
              )}
              {!(message.meta?.askUserQuestion && (message.meta?.askUserQuestionStatus || "pending") === "pending") && (
                <div className="assistant-actions">
                  {citedSourcesForMessage(message, citationSources).length > 0 && <button type="button" onClick={() => onOpenMessageSources(message.id)}>查看引用资料</button>}
                  {referencePoolTotalCount > 0 && <button type="button" onClick={() => onOpenReferencePool(message.id)}>把检索结果加入文献包</button>}
                  <button type="button" onClick={() => setPrompt("请基于上面的回答生成一版组合大纲")}>生成组合大纲</button>
                  <button type="button" onClick={() => {
                    const [source] = citedSourcesForMessage(message, citationSources);
                    if (source) saveSourceAsAsset(source);
                  }}>保存到项目</button>
                </div>
              )}
              {message.meta?.commitError && (
                <p className="assistant-save-error" role="status">回答已生成，但保存失败：{message.meta.commitError}</p>
              )}
              {message.meta?.responseError && (
                <p className="assistant-save-error" role="status">回复未完整生成：{message.meta.responseError}</p>
              )}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}
